import { useState, useEffect, useCallback } from 'react';
import {
    doc, onSnapshot, setDoc, getDoc, runTransaction, arrayUnion,
    collection, addDoc, updateDoc, deleteDoc, deleteField, increment, Timestamp,
    query, where, getDocs,
} from 'firebase/firestore';
import { db } from '../firebase';

function generateInitialUserId(uid) {
    const clean = uid.replace(/[^a-z0-9]/gi, '').toLowerCase().slice(0, 10);
    return 'user_' + clean;
}

const normalizeEmail = value => String(value || '').trim().toLowerCase();
const normalizePhone = value => String(value || '').replace(/[^\d]/g, '');

export function useFirestoreData(uid) {
    const [records, setRecordsLocal] = useState([]);
    const [weights, setWeightsLocal] = useState([]);
    const [templates, setTemplatesLocal] = useState([]);
    const [goals, setGoalsLocal] = useState([]);
    const [promotions, setPromotionsLocal] = useState([]);
    const [friends, setFriendsLocal] = useState([]);
    const [friendRequests, setFriendRequestsLocal] = useState([]);
    const [sentFriendRequests, setSentFriendRequestsLocal] = useState([]);
    const [notifications, setNotificationsLocal] = useState([]);
    const [nickname, setNicknameLocal] = useState(undefined);
    const [userId, setUserIdLocal] = useState(undefined);
    const [profile, setProfileLocal] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!uid) { setLoading(false); return; }
        setLoading(true);
        const ref = doc(db, 'users', uid);
        const unsub = onSnapshot(ref, snap => {
            const d = snap.exists() ? snap.data() : {};
            setRecordsLocal(d.records || []);
            setWeightsLocal(d.weights || []);
            setTemplatesLocal(d.templates || []);
            setGoalsLocal(d.goals || []);
            setPromotionsLocal(d.promotions || []);
            setFriendsLocal(d.friends || []);
            setFriendRequestsLocal(d.friendRequests || []);
            setSentFriendRequestsLocal(d.sentFriendRequests || []);
            setNotificationsLocal(d.notifications || []);
            setNicknameLocal(d.nickname !== undefined ? d.nickname : null);
            setProfileLocal({
                birthDate: d.birthDate || '',
                gender: d.gender || '',
                email: d.email || '',
                phone: d.phone || '',
            });

            if (d.userId) {
                setUserIdLocal(d.userId);
            } else {
                const generated = generateInitialUserId(uid);
                setUserIdLocal(generated);
                setDoc(ref, { userId: generated }, { merge: true });
                setDoc(doc(db, 'userIds', generated), { uid });
            }

            setLoading(false);
        });
        return unsub;
    }, [uid]);

    const save = (field, value) =>
        setDoc(doc(db, 'users', uid), { [field]: value }, { merge: true });

    const setRecords = v => { setRecordsLocal(v); save('records', v); };
    const setWeights = v => { setWeightsLocal(v); save('weights', v); };
    const setTemplates = v => { setTemplatesLocal(v); save('templates', v); };
    const setGoals = v => { setGoalsLocal(v); save('goals', v); };
    const setPromotions = v => { setPromotionsLocal(v); save('promotions', v); };

    const setNickname = (v) => {
        setNicknameLocal(v);
        save('nickname', v);
        if (userId) {
            setDoc(doc(db, 'userIds', userId), { uid, nickname: v }, { merge: true });
        }
    };

    const isProfileComplete = Boolean(
        nickname && profile.birthDate && profile.gender && profile.email && profile.phone
    );

    const saveProfileSetup = async ({ nickname: nextNickname, birthDate, gender, email, phone }) => {
        const cleanNickname = String(nextNickname || '').trim();
        const cleanBirthDate = String(birthDate || '').trim();
        const cleanGender = String(gender || '').trim();
        const cleanEmail = normalizeEmail(email);
        const cleanPhone = normalizePhone(phone);
        if (!cleanNickname || !cleanBirthDate || !cleanGender || !cleanEmail || !cleanPhone) {
            throw new Error('required');
        }

        await runTransaction(db, async (tx) => {
            const userRef = doc(db, 'users', uid);
            const userSnap = await tx.get(userRef);
            const current = userSnap.exists() ? userSnap.data() : {};
            const emailRef = doc(db, 'emailIds', cleanEmail);
            const phoneRef = doc(db, 'phoneIds', cleanPhone);
            const emailSnap = await tx.get(emailRef);
            const phoneSnap = await tx.get(phoneRef);

            if (emailSnap.exists() && emailSnap.data().uid !== uid) throw new Error('email_taken');
            if (phoneSnap.exists() && phoneSnap.data().uid !== uid) throw new Error('phone_taken');

            if (current.email && normalizeEmail(current.email) !== cleanEmail) {
                tx.delete(doc(db, 'emailIds', normalizeEmail(current.email)));
            }
            if (current.phone && normalizePhone(current.phone) !== cleanPhone) {
                tx.delete(doc(db, 'phoneIds', normalizePhone(current.phone)));
            }

            tx.set(emailRef, { uid }, { merge: true });
            tx.set(phoneRef, { uid }, { merge: true });
            tx.set(userRef, {
                nickname: cleanNickname,
                birthDate: cleanBirthDate,
                gender: cleanGender,
                email: cleanEmail,
                phone: cleanPhone,
            }, { merge: true });
            if (userId) {
                tx.set(doc(db, 'userIds', userId), { uid, nickname: cleanNickname }, { merge: true });
            }
        });

        setNicknameLocal(cleanNickname);
        setProfileLocal({ birthDate: cleanBirthDate, gender: cleanGender, email: cleanEmail, phone: cleanPhone });
    };

    const checkUserIdAvailable = async (newId) => {
        if (newId === userId) return 'same';
        const snap = await getDoc(doc(db, 'userIds', newId));
        if (!snap.exists()) return 'available';
        if (snap.data().uid === uid) return 'same';
        return 'taken';
    };

    const updateMyPosts = async (patch) => {
        if (!uid) return;
        const snap = await getDocs(query(collection(db, 'posts'), where('uid', '==', uid)));
        await Promise.all(snap.docs.map(d => updateDoc(d.ref, patch)));
    };

    const updateUserId = async (newId) => {
        await runTransaction(db, async (tx) => {
            const newRef = doc(db, 'userIds', newId);
            const newSnap = await tx.get(newRef);
            if (newSnap.exists() && newSnap.data().uid !== uid) throw new Error('already_taken');
            if (userId) {
                const oldRef = doc(db, 'userIds', userId);
                const oldSnap = await tx.get(oldRef);
                if (oldSnap.exists()) tx.delete(oldRef);
            }
            tx.set(newRef, { uid, nickname: nickname || '' });
            tx.set(doc(db, 'users', uid), { userId: newId }, { merge: true });
        });
        setUserIdLocal(newId);
        updateMyPosts({ userId: newId }).catch(console.error);
    };

    const makeNotification = ({ id, type, title, body, fromUid, fromUserId, fromNickname }) => ({
        id,
        type,
        title,
        body,
        fromUid: fromUid || '',
        fromUserId: fromUserId || '',
        fromNickname: fromNickname || '',
        read: false,
        createdAt: new Date().toISOString(),
    });

    const addFriend = async (targetUserId, currentFriends) => {
        const idSnap = await getDoc(doc(db, 'userIds', targetUserId));
        if (!idSnap.exists()) throw new Error('not_found');
        const { uid: targetUid, nickname: targetNickname } = idSnap.data();
        if (targetUid === uid) throw new Error('self');
        if ((currentFriends || friends).some(f => f.uid === targetUid)) throw new Error('already');

        const targetSnap = await getDoc(doc(db, 'users', targetUid));
        const targetData = targetSnap.exists() ? targetSnap.data() : {};
        const existingRequest = (targetData.friendRequests || []).some(r => r.fromUid === uid && r.status === 'pending');
        const alreadySent = sentFriendRequests.some(r => r.toUid === targetUid && r.status === 'pending');
        if (existingRequest || alreadySent) throw new Error('pending');

        const now = new Date().toISOString();
        const requestId = `friend_request_${uid}_${Date.now()}`;
        const request = {
            id: requestId,
            fromUid: uid,
            fromUserId: userId || '',
            fromNickname: nickname || userId || '사용자',
            status: 'pending',
            createdAt: now,
        };
        const sentRequest = {
            id: requestId,
            toUid: targetUid,
            toUserId: targetUserId,
            toNickname: targetNickname || targetUserId,
            status: 'pending',
            createdAt: now,
        };
        const notification = makeNotification({
            id: requestId,
            type: 'friend_request',
            title: '친구 요청',
            body: `${request.fromNickname}님이 친구 요청을 보냈어요.`,
            fromUid: uid,
            fromUserId: request.fromUserId,
            fromNickname: request.fromNickname,
        });

        await setDoc(doc(db, 'users', targetUid), {
            friendRequests: arrayUnion(request),
            notifications: arrayUnion(notification),
        }, { merge: true });
        const nextSent = [...sentFriendRequests, sentRequest];
        setSentFriendRequestsLocal(nextSent);
        await save('sentFriendRequests', nextSent);

        return { uid: targetUid, userId: targetUserId, nickname: targetNickname || targetUserId };
    };

    const acceptFriendRequest = async (request) => {
        if (!uid || !request?.fromUid) return;
        const addedAt = new Date().toISOString();
        const requesterEntry = {
            uid: request.fromUid,
            userId: request.fromUserId || '',
            nickname: request.fromNickname || request.fromUserId || '사용자',
            addedAt,
        };
        const myEntry = {
            uid,
            userId: userId || '',
            nickname: nickname || userId || '사용자',
            addedAt,
        };
        const nextFriends = friends.some(f => f.uid === request.fromUid) ? friends : [...friends, requesterEntry];
        const nextRequests = friendRequests.filter(r => r.id !== request.id);
        const nextNotifications = notifications.map(n => (
            n.id === request.id || (n.type === 'friend_request' && n.fromUid === request.fromUid)
                ? { ...n, read: true }
                : n
        ));

        setFriendsLocal(nextFriends);
        setFriendRequestsLocal(nextRequests);
        setNotificationsLocal(nextNotifications);
        await setDoc(doc(db, 'users', uid), {
            friends: nextFriends,
            friendRequests: nextRequests,
            notifications: nextNotifications,
        }, { merge: true });
        const requesterRef = doc(db, 'users', request.fromUid);
        const requesterSnap = await getDoc(requesterRef);
        const requesterSent = requesterSnap.exists()
            ? (requesterSnap.data().sentFriendRequests || []).filter(r => r.id !== request.id)
            : [];
        await setDoc(requesterRef, {
            friends: arrayUnion(myEntry),
            sentFriendRequests: requesterSent,
            notifications: arrayUnion(makeNotification({
                id: `friend_accept_${uid}_${Date.now()}`,
                type: 'friend_accept',
                title: '친구 요청 수락',
                body: `${myEntry.nickname}님이 친구 요청을 수락했어요.`,
                fromUid: uid,
                fromUserId: myEntry.userId,
                fromNickname: myEntry.nickname,
            })),
        }, { merge: true });
    };

    const declineFriendRequest = async (request) => {
        if (!uid || !request?.id) return;
        const nextRequests = friendRequests.filter(r => r.id !== request.id);
        const nextNotifications = notifications.map(n => (
            n.id === request.id || (n.type === 'friend_request' && n.fromUid === request.fromUid)
                ? { ...n, read: true }
                : n
        ));
        setFriendRequestsLocal(nextRequests);
        setNotificationsLocal(nextNotifications);
        await setDoc(doc(db, 'users', uid), {
            friendRequests: nextRequests,
            notifications: nextNotifications,
        }, { merge: true });
        const requesterRef = doc(db, 'users', request.fromUid);
        const requesterSnap = await getDoc(requesterRef);
        if (requesterSnap.exists()) {
            const nextSent = (requesterSnap.data().sentFriendRequests || []).filter(r => r.id !== request.id);
            await setDoc(requesterRef, { sentFriendRequests: nextSent }, { merge: true });
        }
    };

    const cancelFriendRequest = async (request) => {
        if (!uid || !request?.id || !request?.toUid) return;
        const nextSent = sentFriendRequests.filter(r => r.id !== request.id);
        setSentFriendRequestsLocal(nextSent);
        await save('sentFriendRequests', nextSent);

        const targetRef = doc(db, 'users', request.toUid);
        const targetSnap = await getDoc(targetRef);
        if (!targetSnap.exists()) return;
        const targetData = targetSnap.data();
        const nextRequests = (targetData.friendRequests || []).filter(r => r.id !== request.id);
        const nextNotifications = (targetData.notifications || []).filter(n => n.id !== request.id);
        await setDoc(targetRef, {
            friendRequests: nextRequests,
            notifications: nextNotifications,
        }, { merge: true });
    };

    const markNotificationRead = async (notificationId) => {
        if (!uid || !notificationId) return;
        const next = notifications.map(n => n.id === notificationId ? { ...n, read: true } : n);
        setNotificationsLocal(next);
        await save('notifications', next);
    };

    const markAllNotificationsRead = async () => {
        if (!uid) return;
        const next = notifications.map(n => ({ ...n, read: true }));
        setNotificationsLocal(next);
        await save('notifications', next);
    };

    const removeFriend = async (targetUid) => {
        if (!uid || !targetUid) return;
        let nextFriends = friends.filter(f => f.uid !== targetUid);
        await runTransaction(db, async (tx) => {
            const myRef = doc(db, 'users', uid);
            const targetRef = doc(db, 'users', targetUid);
            const [mySnap, targetSnap] = await Promise.all([tx.get(myRef), tx.get(targetRef)]);
            const myData = mySnap.exists() ? mySnap.data() : {};
            const targetData = targetSnap.exists() ? targetSnap.data() : {};
            nextFriends = (myData.friends || friends).filter(f => f.uid !== targetUid);
            const targetNextFriends = (targetData.friends || []).filter(f => f.uid !== uid);
            tx.set(myRef, { friends: nextFriends }, { merge: true });
            if (targetSnap.exists()) {
                tx.set(targetRef, { friends: targetNextFriends }, { merge: true });
            }
        });
        setFriendsLocal(nextFriends);
    };

    const getFriendData = useCallback(async (targetUid) => {
        const snap = await getDoc(doc(db, 'users', targetUid));
        return snap.exists() ? snap.data() : null;
    }, []);

    const createPost = async (record, visibility) => {
        if (!uid || visibility === 'private') return;
        const friendUids = (friends || []).map(f => f.uid);
        const visibleUids = visibility === 'friends' ? [uid, ...friendUids] : [uid];

        const post = {
            uid,
            nickname: nickname || '',
            userId: userId || '',
            recordId: record.recordId || '',
            visibility,
            visibleUids,
            sport: record.sport || '',
            date: record.date || '',
            duration: record.duration || '',
            focus: record.focus || '',
            postMessage: record.postMessage || '',
            condScore: record.condScore || 3,
            fatScore: record.fatScore || 3,
            sparring: record.sparring || false,
            sparringRounds: record.sparringRounds || '',
            sparringMin: record.sparringMin || '',
            techniques: (record.techniques || []).slice(0, 5).map(t => ({
                name: t.name || '', point: t.point || '', result: t.result || '', category: t.category || '', position: t.position || '',
            })),
            sparringRoundsList: (record.sparringRoundsList || []).slice(0, 5).map(r => ({
                result: r.result || '',
                opponentLevel: r.opponentLevel || '',
                tapouts: r.tapouts || '',
                roundCount: r.roundCount || '',
                roundMinutes: r.roundMinutes || '',
                notes: r.notes || '',
                opponent: r.opponent || null,
            })),
            exercises: (record.exercises || []).slice(0, 5).map(ex => ({
                name: ex.name || '', muscleGroup: ex.muscleGroup || '',
            })),
            muscleGroups: record.muscleGroups || [],
            distance: record.distance || '',
            calories: record.calories || '',
            avgHR: record.avgHR || '',
            pace: record.pace || '',
            power: record.power || '',
            strikingDrills: (record.strikingDrills || []).filter(d => d.type),
            drillTime: record.drillTime || '',
            createdAt: Timestamp.now(),
            likeCount: 0,
            likes: {},
        };

        const ref = await addDoc(collection(db, 'posts'), post);
        return ref.id;
    };

    const createPromotionPost = async (promotion, visibility) => {
        if (!uid) return;
        const friendUids = (friends || []).map(f => f.uid);
        const visibleUids = visibility === 'friends' ? [uid, ...friendUids] : [uid];
        const post = {
            type: 'promotion',
            uid,
            nickname: nickname || '',
            userId: userId || '',
            visibility,
            visibleUids,
            promotionId: promotion.id || '',
            date: promotion.date || '',
            belt: promotion.belt || '',
            stripe: promotion.stripe || 0,
            place: promotion.place || '',
            instructor: promotion.instructor || '',
            postMessage: promotion.message || '',
            createdAt: Timestamp.now(),
            likeCount: 0,
            likes: {},
        };
        const ref = await addDoc(collection(db, 'posts'), post);
        return ref.id;
    };

    const updatePromotionPost = async (postId, promotion, visibility) => {
        if (!uid || !postId) return;
        const friendUids = (friends || []).map(f => f.uid);
        const visibleUids = visibility === 'friends' ? [uid, ...friendUids] : [uid];
        await updateDoc(doc(db, 'posts', postId), {
            visibility,
            visibleUids,
            date: promotion.date || '',
            belt: promotion.belt || '',
            stripe: promotion.stripe || 0,
            place: promotion.place || '',
            instructor: promotion.instructor || '',
            postMessage: promotion.message || '',
        });
    };

    const deletePromotionPostForPromotion = async (promotion) => {
        if (!uid || !promotion) return;
        if (promotion.postId) {
            await deletePost(promotion.postId);
            return;
        }
        const snap = await getDocs(query(collection(db, 'posts'), where('uid', '==', uid)));
        const matches = snap.docs.filter(d => {
            const p = d.data();
            return p.type === 'promotion'
                && ((promotion.id && p.promotionId === promotion.id)
                    || (p.date === promotion.date && p.belt === promotion.belt && String(p.stripe || 0) === String(promotion.stripe || 0)));
        });
        await Promise.all(matches.map(d => deleteDoc(d.ref)));
    };

    const updatePost = async (postId, record, visibility) => {
        if (!uid || !postId) return;
        const friendUids = (friends || []).map(f => f.uid);
        const visibleUids = visibility === 'friends' ? [uid, ...friendUids] : [uid];
        const postRef = doc(db, 'posts', postId);
        await updateDoc(postRef, {
            visibility,
            visibleUids,
            recordId: record.recordId || '',
            sport: record.sport || '',
            date: record.date || '',
            duration: record.duration || '',
            focus: record.focus || '',
            postMessage: record.postMessage || '',
            condScore: record.condScore || 3,
            fatScore: record.fatScore || 3,
            sparring: record.sparring || false,
            sparringRounds: record.sparringRounds || '',
            techniques: (record.techniques || []).slice(0, 5).map(t => ({
                name: t.name || '', point: t.point || '', result: t.result || '', category: t.category || '', position: t.position || '',
            })),
            sparringRoundsList: (record.sparringRoundsList || []).slice(0, 5).map(r => ({
                result: r.result || '',
                opponentLevel: r.opponentLevel || '',
                tapouts: r.tapouts || '',
                roundCount: r.roundCount || '',
                roundMinutes: r.roundMinutes || '',
                notes: r.notes || '',
                opponent: r.opponent || null,
            })),
            exercises: (record.exercises || []).slice(0, 5).map(ex => ({
                name: ex.name || '', muscleGroup: ex.muscleGroup || '',
            })),
            muscleGroups: record.muscleGroups || [],
            distance: record.distance || '',
            avgHR: record.avgHR || '',
            pace: record.pace || '',
            power: record.power || '',
            strikingDrills: (record.strikingDrills || []).filter(d => d.type),
            drillTime: record.drillTime || '',
        });
    };

    const deletePost = async (postId) => {
        if (!uid || !postId) return;
        await deleteDoc(doc(db, 'posts', postId));
    };

    const deletePostForRecord = async (record) => {
        if (!uid || !record) return;
        if (record.postId) {
            await deletePost(record.postId);
            return;
        }
        const snap = await getDocs(query(collection(db, 'posts'), where('uid', '==', uid)));
        const matches = snap.docs.filter(d => {
            const p = d.data();
            if (record.recordId && p.recordId === record.recordId) return true;
            return p.date === record.date
                && p.sport === record.sport
                && String(p.duration || '') === String(record.duration || '')
                && String(p.focus || '') === String(record.focus || '');
        });
        await Promise.all(matches.map(d => deleteDoc(d.ref)));
    };

    const toggleLike = async (postId, isCurrentlyLiked) => {
        if (!uid) return;
        const postRef = doc(db, 'posts', postId);
        if (isCurrentlyLiked) {
            await updateDoc(postRef, {
                [`likes.${uid}`]: deleteField(),
                likeCount: increment(-1),
            });
        } else {
            await updateDoc(postRef, {
                [`likes.${uid}`]: { nickname: nickname || '', likedAt: Timestamp.now() },
                likeCount: increment(1),
            });
        }
    };

    return {
        records, setRecords,
        weights, setWeights,
        templates, setTemplates,
        goals, setGoals,
        promotions, setPromotions,
        friends, friendRequests, sentFriendRequests, notifications,
        addFriend, removeFriend, getFriendData,
        acceptFriendRequest, declineFriendRequest, cancelFriendRequest, markNotificationRead, markAllNotificationsRead,
        nickname, setNickname,
        profile, isProfileComplete, saveProfileSetup,
        userId, checkUserIdAvailable, updateUserId,
        createPost, createPromotionPost, updatePromotionPost, deletePromotionPostForPromotion, updatePost, deletePost, deletePostForRecord, toggleLike,
        loading,
    };
}
